import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";
import { useImmerReducer } from "use-immer";
import { useDropdownApis } from "../../../shared";
import { convertDate, errorMessage, successMessage } from "../../../utils";
import {
  createPromosOrNotificationsApi,
  updatePromosOrNotificationsApi,
} from "../api/promosAndNotificationsApi";
import { usePromosAndNotificationsDetails } from "./usePromosAndNotificationsDetails";

const reducer = (draft, action) => {
  switch (action.type) {
    case "CHANGE_DETAILS":
      draft.details[action.payload.name] = action.payload.value;
      break;
    case "SET_LOADING":
      draft.loading[action.payload.name] = action.payload.value;
      break;
    case "SET_DIALOG":
      draft.dialog[action.payload.name] = action.payload.value;
      break;
    case "SET_DETAILS":
      draft.details = action.payload;
      break;
    default:
      break;
  }
};

export const useManagePromosAndNotifications = () => {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [state, dispatch] = useImmerReducer(reducer, {
    details: {
      name: "",
      label: "",
      description: "",
      type: "notification",
      status: "",
      start_date: new Date(),
      end_date: null,
      stores: [],
      image: { file: null, url: "" },
    },
    loading: {
      published: false,
      draft: false,
    },
    dialog: { publish: false },
  });
  const { storeList } = useDropdownApis({ isStoreList: true });

  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const {
    state: { details, validityAndStoreDetails, loading },
  } = usePromosAndNotificationsDetails({ enableApi: isEditMode });

  useEffect(() => {
    if (!loading.list && details) {
      const { name, label, description, type, status, image_path } = details;
      const { start_date, end_date, stores } = validityAndStoreDetails;

      if (status !== "draft") {
        errorMessage(
          `${type} cannot be edited. Only items in Draft status can be modified.`,
        );
        return navigate(`/promos-and-notifications/${id}`);
      }

      dispatch({
        type: "SET_DETAILS",
        payload: {
          name,
          label,
          description,
          type,
          status,
          start_date,
          end_date,
          stores,
          image: { file: null, url: image_path },
        },
      });
    }
  }, [loading.list]);

  const title = isEditMode ? state.details.name : "New Promo or Notification";

  const breadcrumbLinks = React.useMemo(
    () => [
      {
        name: "Dashboard",
        href: "/",
      },
      {
        name: "Promos & Notifications",
        href: "/promos-and-notifications",
      },
      ...(isEditMode
        ? [
            {
              name: title,
              href: `/promos-and-notifications/${id}`,
            },
          ]
        : []),
      {
        name: isEditMode ? "Edit" : "Create",
      },
    ],
    [title],
  );

  const validator = useRef(
    new SimpleReactValidator({
      element: (message) => message,
      autoForceUpdate: { forceUpdate: setForceUpdate },
      validators: {
        atLeastOneIdentifier: {
          message:
            "At least one field is required: Label, Description, or Image.",
          rule: ({ label, description, image }) => {
            return Boolean(label || description || image.file || image.url);
          },
          required: true,
        },
        endDate: {
          message: "End date must be greater than or equal to start date",
          rule: ({ start_date, end_date }) => {
            return (
              end_date &&
              moment(end_date).isSameOrAfter(moment(start_date), "day")
            );
          },
        },
      },
    }),
  );

  const createPromoOrNotification = async (payload) => {
    dispatch({
      type: "SET_LOADING",
      payload: { name: payload.status, value: true },
    });

    try {
      const response = await createPromosOrNotificationsApi(
        convertToFormData(payload),
      );
      const { success, message } = response;

      if (success) {
        successMessage(message);
        navigate("/promos-and-notifications");
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response.data?.message || "Failed to create the " + payload.type;
      errorMessage(message);
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: { name: payload.status, value: false },
      });
    }
  };

  const updatePromoOrNotification = async (payload, id) => {
    dispatch({
      type: "SET_LOADING",
      payload: { name: payload.status, value: true },
    });

    try {
      payload._method = "PUT";

      const response = await updatePromosOrNotificationsApi(
        id,
        convertToFormData(payload),
      );
      const { success, message } = response;

      if (success) {
        successMessage(message);
        navigate(`/promos-and-notifications/${id}`, { replace: true });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response.data?.message || "Failed to update the " + payload.type;
      errorMessage(message);
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: { name: payload.status, value: false },
      });
    }
  };

  const convertToFormData = (payload) => {
    let formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => formData.append(`${key}[]`, v));
      } else {
        formData.append(key, value);
      }
    });
    return formData;
  };

  const onResetImage = () => {
    dispatch({
      type: "CHANGE_DETAILS",
      payload: {
        name: "image",
        value: { file: null, url: "" },
      },
    });
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    dispatch({
      type: "CHANGE_DETAILS",
      payload: {
        name,
        value:
          name === "image"
            ? { file: value[0], url: URL.createObjectURL(value[0]) }
            : value,
      },
    });
  };

  const togglePublishDialog = (status) => {
    dispatch({
      type: "SET_DIALOG",
      payload: { name: "publish", value: status },
    });
  };

  const onSubmit = async ({ status, confirm = true }) => {
    if (validator.current.allValid()) {
      if (status === "published") {
        if (!confirm) return togglePublishDialog(true);
        else togglePublishDialog(false);
      }

      const {
        name,
        start_date,
        end_date,
        label,
        description,
        type,
        stores,
        image,
      } = state.details;

      const payload = {
        name,
        type,
        start_date: convertDate({
          date: start_date,
          format: "DD-MM-YYYY",
        }),
      };

      payload.status = status;

      if (end_date) {
        payload.end_date = convertDate({
          date: state.details.end_date,
          format: "DD-MM-YYYY",
        });
      }
      if (label) payload.label = label;
      if (description) payload.description = description;
      if (image?.file) payload.image_path = image?.file;
      if (stores?.length > 0)
        payload.store_id = stores.map((store) => store.id);

      if (isEditMode) {
        if (!payload?.end_date) payload.end_date = "";
        if (!image.url) payload.image_path = "";
        if (!payload?.label) payload.label = "";
        if (!payload?.description) payload.description = "";

        await updatePromoOrNotification(payload, id);
      } else {
        await createPromoOrNotification(payload);
      }
    } else {
      validator.current.showMessages();
      setForceUpdate(forceUpdate + 1);
    }
  };

  return {
    state,
    loading,
    title,
    isEditMode,
    breadcrumbLinks,
    validator,
    storeList,
    onSubmit,
    onChange,
    onResetImage,
    dispatch,
    togglePublishDialog,
  };
};
